import type { ElementType } from 'react';
import { Flipper } from 'react-flip-toolkit';
import styled, { css, keyframes } from 'styled-components';

import { BiggerSubtitle, style } from '@genially/design-system';
import DisplayLeftIcon from '@genially/icons/lib/display-left/DisplayLeftIcon';
import DisplayRightIcon from '@genially/icons/lib/display-right/DisplayRightIcon';

import { GridColumnGap, GridRowGap, MaxItemHeight, MaxItemWidth } from './constants';

interface FullWidthProps {
  $isFullWidthEnabled?: boolean;
}

export const RightWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: size.units(1),
}));

export const FullWidthTitleWrapper = styled.div(({ theme: { size } }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: size.units(1),
}));

interface ScrollWrapperProps {
  $isVerticalScroll?: boolean;
  $hasScrollStart?: boolean;
  $hasScrollEnd?: boolean;
}

const NavigationButton = styled.div(({ theme: { borderRadius, size, color } }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: size.units(5),
  height: size.units(5),
  borderRadius: borderRadius.medium,
  cursor: 'pointer',
  backgroundColor: color.background.tertiary.default(0.4),
}));

export const LeftNavigationButton = styled(NavigationButton)({});

export const RightNavigationButton = styled(NavigationButton)({});

export const StyledLeftArrow = styled(DisplayLeftIcon)(({ theme: { color, size } }) => ({
  width: size.units(2),
  height: size.units(2),
  color: color.content.reversed.default(),
}));

export const StyledRightArrow = styled(DisplayRightIcon)(
  ({ theme: { color, size } }) => ({
    width: size.units(2),
    height: size.units(2),
    color: color.content.reversed.default(),
  }),
);

export const ScrollWrapper = styled.div<ScrollWrapperProps>(
  ({
    theme: { color, size, transition },
    $isVerticalScroll,
    $hasScrollStart,
    $hasScrollEnd,
  }) => ({
    position: 'relative',
    minWidth: 0,
    minHeight: 0,
    height: $isVerticalScroll ? undefined : '100%',
    flexGrow: $isVerticalScroll ? undefined : 1,
    backgroundColor: color.background.reversed.default(),
    borderRadius: `${size.units(3)} ${size.units(3)} 0 0`,
    flex: '1 1 0',

    '::after, ::before': {
      position: 'absolute',
      content: '""',
      left: $isVerticalScroll ? 0 : undefined,
      top: $isVerticalScroll ? undefined : 0,
      width: $isVerticalScroll ? '100%' : size.units(9),
      height: $isVerticalScroll ? size.units(9) : '100%',
      pointerEvents: 'none',
      transition: transition.standard('opacity'),
      background: `linear-gradient(${$isVerticalScroll ? '0deg' : '270deg'}, rgba(255, 255, 255, 0), ${color.background.reversed.default()})`,
    },

    '::before': {
      top: $isVerticalScroll ? -1 : undefined,
      left: $isVerticalScroll ? undefined : -1,
      opacity: $hasScrollStart ? 1 : 0,
      borderRadius: `${size.units(3)} ${size.units(3)} 0 0`,
    },

    '::after': {
      bottom: $isVerticalScroll ? -1 : undefined,
      right: $isVerticalScroll ? undefined : -1,
      transform: 'rotate(180deg)',
      opacity: $hasScrollEnd ? 1 : 0,
    },

    [`${LeftNavigationButton}, ${RightNavigationButton}`]: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      transition: transition.standard('opacity'),
      zIndex: 1,
    },

    [LeftNavigationButton]: {
      left: size.units(5.25),

      opacity: $hasScrollStart ? 1 : 0,
    },

    [RightNavigationButton]: {
      right: size.units(5.25),
      opacity: $hasScrollEnd ? 1 : 0,
    },
  }),
);

interface PaddingWrapperProps {
  $isFullWidthEnabled?: boolean;
}

export const PaddingWrapper = styled.div<PaddingWrapperProps>(
  ({ theme: { size }, $isFullWidthEnabled }) => ({
    height: '100%',
    padding: `${size.units(2)} ${$isFullWidthEnabled ? size.units(2) : size.units(3)} ${size.units(2)} ${$isFullWidthEnabled ? 0 : size.units(3)}`,
  }),
);

export const ContentWrapper = styled.div(({ theme }) => ({
  overflowY: 'auto',
  overflowX: 'hidden',
  height: '100%',

  overflowAnchor: 'none',

  paddingRight: theme.size.units(1),

  ...style.mixins.scrollbar(theme),
}));

export const Content = styled.div<FullWidthProps>(
  ({ theme: { size }, $isFullWidthEnabled }) => ({
    width: '100%',
    height: $isFullWidthEnabled ? '80%' : '100%',
    display: 'flex',
    flexDirection: $isFullWidthEnabled ? 'row' : 'column',
    paddingTop: 0,
    gap: $isFullWidthEnabled ? size.units(5) : size.units(0.5),

    ...($isFullWidthEnabled && {
      alignItems: 'center',
      overflow: 'hidden',
    }),

    [ScrollWrapper]: {
      marginTop: $isFullWidthEnabled ? undefined : size.units(-2),
    },
  }),
);

export const StyledFlipper = styled(Flipper)({
  height: '100%',
});

export const EmptyItemWrapper = styled.div({
  width: '100%',
});

export const RankingItemWrapper = styled.div({
  position: 'relative',

  [EmptyItemWrapper]: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
});

interface ParticipantsWrapperProps extends FullWidthProps {
  $width?: number;
}

export const ParticipantsWrapper = styled.div<ParticipantsWrapperProps>(
  ({ theme: { color, size }, $isFullWidthEnabled, $width }) => ({
    display: 'grid',
    width: $isFullWidthEnabled ? $width : '100%',
    height: $isFullWidthEnabled ? '100%' : undefined,
    rowGap: GridRowGap,

    ...($isFullWidthEnabled && {
      paddingLeft: GridColumnGap / 2,
      minWidth: 0,
      columnGap: GridColumnGap,
      gridTemplateRows: `repeat(auto-fill, ${MaxItemHeight}px)`,
      gridAutoColumns: MaxItemWidth,
      gridAutoFlow: 'column',

      // eslint-disable-next-line @genially/force-functional-colors-in-context
      background: `linear-gradient(to right, ${color.border.quaternary.default()} 2px, transparent 2px)`,
      backgroundSize: `${MaxItemWidth + GridColumnGap}px calc(100% - ${size.units(2)})`,
      backgroundRepeat: 'repeat-x',
      backgroundPositionY: 'center',
    }),
  }),
);

export const StyledBiggerSubtitle = styled(BiggerSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
  textAlign: 'center',
}));

const animateBackground = keyframes({
  '0%': {
    transform: 'translate(-50%, -50%) rotate(0)',
  },
  '100%': {
    transform: 'translate(-50%, -50%) rotate(360deg)',
  },
});

const withBackgroundAnimation = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    () => css`
      animation: ${animateBackground} 30s linear infinite;
    `,
  );

export const HeaderBackground = withBackgroundAnimation(
  styled.div(({ theme: { size } }) => ({
    width: size.units(63),
    height: size.units(63),
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#0d1b2a',
    backgroundImage:
      'radial-gradient(circle at center, #0d1b2a, transparent 40%), repeating-conic-gradient(from 0deg, #0d1b2a, #0d1b2a 10deg, #1a2b3d 10deg, #1a2b3d 20deg)',
    backgroundSize: 'cover',
    opacity: 0.6,
  })),
);

export const StyledCanvas = styled.canvas({
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
});

export const HeaderWrapper = styled.div<FullWidthProps>(
  ({ theme: { size }, $isFullWidthEnabled }) => ({
    width: $isFullWidthEnabled ? size.units(17) : '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    paddingTop: $isFullWidthEnabled ? undefined : size.units(3),
    paddingBottom: $isFullWidthEnabled ? undefined : size.units(5),
    gap: size.units(3),
    flexShrink: 0,
    height: $isFullWidthEnabled ? '100%' : undefined,

    [HeaderBackground]: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      zIndex: -1,
    },

    [StyledCanvas]: {
      position: 'absolute',
      top: 0,
      left: 0,
    },
  }),
);

export const HeaderAvatarsWrapper = styled.div<FullWidthProps>(
  ({ theme: { size }, $isFullWidthEnabled }) => ({
    display: 'flex',
    position: 'relative',
    width: '100%',
    padding: `0 ${size.units(1)}`,
    flexDirection: $isFullWidthEnabled ? 'column' : 'row',
    gap: size.units(2),
    justifyContent: 'space-around',

    alignItems: $isFullWidthEnabled ? 'center' : 'flex-end',
    height: $isFullWidthEnabled ? '100%' : undefined,
    maxHeight: $isFullWidthEnabled ? size.units(62.5) : undefined,

    ...(!$isFullWidthEnabled && {
      '& > *:nth-child(1)': {
        marginBottom: size.units(1),
      },

      '& > *:nth-child(2)': {
        marginBottom: size.units(2),
      },
    }),
  }),
);

export const Wrapper = styled.div<FullWidthProps>(
  ({ theme: { size }, $isFullWidthEnabled }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: $isFullWidthEnabled ? 'column' : 'row',
    position: 'relative',

    [FullWidthTitleWrapper]: {
      marginBottom: size.units(6.5),
    },
  }),
);
